<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { adminApi, type AdminOverview } from '../api'
import AppShell from '../components/AppShell.vue'
import ErrorState from '../components/ErrorState.vue'
import LoadingState from '../components/LoadingState.vue'
import { useSession } from '../session'
import { themeName } from '../themeData'

defineEmits<{
  toggleTheme: []
}>()

const session = useSession()
const overview = ref<AdminOverview | null>(null)
const loading = ref(false)
const error = ref('')

const queueTotal = computed(() =>
  overview.value?.queue.statuses.reduce((sum, item) => sum + item.jobs, 0) ?? 0,
)
const queueActive = computed(() =>
  overview.value?.queue.statuses
    .filter((item) => ['pending', 'queued', 'processing'].includes(item.status))
    .reduce((sum, item) => sum + item.jobs, 0) ?? 0,
)
const queueFailed = computed(() =>
  overview.value?.queue.statuses.find((item) => item.status === 'failed')?.jobs ?? 0,
)

async function load(): Promise<void> {
  if (!session.isAdmin.value) return
  loading.value = true
  error.value = ''
  try {
    overview.value = await adminApi.overview()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось загрузить данные.'
  } finally {
    loading.value = false
  }
}

function number(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value)
}

function duration(value: number): string {
  if (value < 1_000) return `${value} мс`
  if (value < 60_000) return `${(value / 1_000).toFixed(1)} с`
  return `${(value / 60_000).toFixed(1)} мин`
}

function date(value: string | null): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function ownerName(owner: AdminOverview['presentations'][number]['owner']): string {
  return owner.firstName
    || (owner.username ? `@${owner.username}` : '')
    || (owner.telegramId ? `TG ${owner.telegramId}` : 'Без Telegram')
}

function presentationStatusLabel(status: string): string {
  return {
    draft: 'Черновик',
    generating: 'Генерируется',
    ready: 'Готова',
    failed: 'Ошибка',
    archived: 'В архиве',
  }[status] ?? status
}

function queueStatusLabel(status: string): string {
  return {
    pending: 'Ожидает',
    queued: 'В очереди',
    processing: 'В работе',
    completed: 'Готово',
    failed: 'Ошибка',
    delayed: 'Отложено',
  }[status] ?? status
}

function stageLabel(stage: string): string {
  return {
    outline: 'План',
    plan: 'План',
    generate: 'Слайды',
    slides: 'Слайды',
    edit: 'Правка слайда',
    slide_edit: 'Правка слайда',
    fit: 'Сокращение текста',
    shorten: 'Сокращение текста',
    theme: 'Подбор темы',
    custom_theme: 'Подбор темы',
    classify: 'Разбор команды',
    classify_command: 'Разбор команды',
    transcribe: 'Расшифровка голоса',
    transcription: 'Расшифровка голоса',
    quality_repair: 'Исправление качества',
  }[stage] ?? stage
}

function jobKindLabel(kind: string): string {
  return {
    png: 'PNG-превью',
    pdf: 'PDF',
    contact: 'Контактный лист',
    revision: 'Вся ревизия',
    slide: 'Один слайд',
    render_revision: 'Вся презентация',
    render_slide: 'Один слайд',
    'render.png': 'PNG-превью',
    'render.pdf': 'PDF',
    'render.contact': 'Контактный лист',
  }[kind] ?? kind
}

onMounted(load)
</script>

<template>
  <AppShell @toggle-theme="$emit('toggleTheme')">
    <section v-if="!session.isAdmin.value" class="admin-login surface-card">
      <span class="admin-login__mark">SSO</span>
      <h1>Вход через панель</h1>
      <p>
        У админки нет отдельного пароля. Откройте GetSlides из панели владельца —
        она передаст короткоживущий одноразовый доступ.
      </p>
      <p v-if="session.state.kind === 'user'" class="form-error">
        Сейчас открыт обычный пользовательский кабинет.
      </p>
    </section>

    <template v-else>
      <section class="page-heading">
        <div>
          <span class="eyebrow">Владелец продукта</span>
          <h1>GetSlides изнутри</h1>
          <p>Презентации, расходы AI и состояние рендера на одном экране.</p>
        </div>
        <button type="button" class="button button--secondary" :disabled="loading" @click="load">
          ↻ Обновить
        </button>
      </section>

      <LoadingState v-if="loading && !overview" title="Собираем статистику" />
      <ErrorState v-else-if="error && !overview" :text="error" @retry="load" />

      <template v-if="overview">
        <section class="admin-metrics">
          <article>
            <span>Презентации</span>
            <strong>{{ number(overview.presentations.length) }}</strong>
            <small>{{ overview.presentations.filter((item) => item.publishedRevision).length }} опубликовано</small>
          </article>
          <article>
            <span>Вызовы AI</span>
            <strong>{{ number(overview.ai.calls) }}</strong>
            <small :class="{ danger: overview.ai.failed > 0 }">{{ number(overview.ai.failed) }} с ошибкой</small>
          </article>
          <article>
            <span>Токены</span>
            <strong>{{ number(overview.ai.tokensIn + overview.ai.tokensOut) }}</strong>
            <small>{{ number(overview.ai.tokensIn) }} вход · {{ number(overview.ai.tokensOut) }} выход</small>
          </article>
          <article>
            <span>Очередь рендера</span>
            <strong>{{ number(queueActive) }}</strong>
            <small :class="{ danger: queueFailed > 0 }">{{ queueFailed ? `${queueFailed} с ошибкой` : `${queueTotal} задач всего` }}</small>
          </article>
        </section>

        <section class="admin-section">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Каталог</span>
              <h2>Все презентации</h2>
            </div>
            <span>{{ overview.presentations.length }}</span>
          </div>
          <div class="admin-table-wrap">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Презентация</th>
                  <th>Владелец</th>
                  <th>Состояние</th>
                  <th>Слайды</th>
                  <th>Обновлена</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="deck in overview.presentations" :key="deck.id">
                  <td>
                    <a v-if="deck.publicUrl" :href="deck.publicUrl" target="_blank">{{ deck.title }}</a>
                    <strong v-else>{{ deck.title }}</strong>
                    <small>{{ deck.aspect }} · {{ themeName(deck.themeId) }} · {{ deck.slug }}</small>
                  </td>
                  <td>
                    {{ ownerName(deck.owner) }}
                    <small v-if="deck.owner.username && deck.owner.firstName">@{{ deck.owner.username }}</small>
                  </td>
                  <td>
                    <span class="table-status" :class="`table-status--${deck.status}`">{{ presentationStatusLabel(deck.status) }}</span>
                    <small>черновик r{{ deck.draftRevision ?? '—' }} · публикация r{{ deck.publishedRevision ?? '—' }}</small>
                  </td>
                  <td>{{ deck.slideCount }}</td>
                  <td>{{ date(deck.updatedAt) }}</td>
                </tr>
                <tr v-if="overview.presentations.length === 0">
                  <td colspan="5" class="admin-table__empty">Презентаций пока нет.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="admin-columns">
          <section class="admin-section">
            <div class="section-heading">
              <div>
                <span class="eyebrow">Расход</span>
                <h2>AI по этапам</h2>
              </div>
              <span>{{ duration(overview.ai.totalMs) }}</span>
            </div>
            <div class="breakdown-list">
              <article v-for="group in overview.ai.groups" :key="`${group.stage}-${group.provider}-${group.model}`">
                <div>
                  <strong>{{ stageLabel(group.stage) }}</strong>
                  <small>{{ [group.provider, group.model].filter(Boolean).join(' · ') }}</small>
                </div>
                <div>
                  <strong>{{ number(group.calls) }} выз.</strong>
                  <small>{{ number(group.tokensIn + group.tokensOut) }} токенов<span v-if="group.failed"> · {{ group.failed }} ошибок</span></small>
                </div>
              </article>
              <p v-if="overview.ai.groups.length === 0" class="muted">Вызовов AI пока нет.</p>
            </div>
          </section>

          <section class="admin-section">
            <div class="section-heading">
              <div>
                <span class="eyebrow">BullMQ + outbox</span>
                <h2>Очередь рендера</h2>
              </div>
            </div>
            <div class="queue-statuses">
              <article v-for="item in overview.queue.statuses" :key="item.status">
                <span :class="`queue-dot queue-dot--${item.status}`"></span>
                <div>
                  <strong>{{ queueStatusLabel(item.status) }}</strong>
                  <small>старейшая: {{ date(item.oldestAt) }}</small>
                </div>
                <b>{{ item.jobs }}</b>
              </article>
              <p v-if="overview.queue.statuses.length === 0" class="muted">Очередь пуста.</p>
            </div>
          </section>
        </div>

        <section v-if="overview.queue.recent.length" class="admin-section">
          <div class="section-heading">
            <div>
              <span class="eyebrow">Последние задачи</span>
              <h2>История рендера</h2>
            </div>
          </div>
          <div class="job-list">
            <article v-for="job in overview.queue.recent" :key="job.id">
              <span class="queue-dot" :class="`queue-dot--${job.status}`"></span>
              <div>
                <strong>{{ jobKindLabel(job.kind) }}</strong>
                <small>{{ job.id }} · {{ date(job.updatedAt) }}</small>
                <p v-if="job.lastError">{{ job.lastError }}</p>
              </div>
              <span>{{ queueStatusLabel(job.status) }} · {{ job.attempts }}/{{ job.maxAttempts }}</span>
            </article>
          </div>
        </section>
      </template>
    </template>
  </AppShell>
</template>
